import React, {createContext, useContext, useState} from 'react';
import {Form, Input, message, Modal, Select} from 'antd';
import {waitlistService} from '../../services/waitlistService';
import {useI18n} from '../../i18n/useI18n';

interface WaitlistContextValue {
    openWaitlist: () => void;
}

const WaitlistContext = createContext<WaitlistContextValue>({
    openWaitlist: () => {
    }
});

export const useWaitlist = () => useContext(WaitlistContext);

export const WaitlistProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {t} = useI18n();
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const openWaitlist = () => setVisible(true);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            await waitlistService.submit(values.name, values.email, values.direction);
            message.success(t('site.waitlistModal.success'));
            form.resetFields();
            setVisible(false);
        } catch (error: any) {
            if (error.errorFields) return;
            message.error(error?.response?.data?.message || t('site.waitlistModal.fail'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <WaitlistContext.Provider value={{openWaitlist}}>
            {children}
            <Modal
                title={t('site.waitlistModal.title')}
                open={visible}
                onOk={handleSubmit}
                onCancel={() => setVisible(false)}
                confirmLoading={submitting}
                okText={t('site.waitlistModal.okText')}
                cancelText={t('site.waitlistModal.cancelText')}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" requiredMark={false} style={{marginTop: 16}}>
                    <Form.Item
                        name="name"
                        rules={[{required: true, message: t('site.waitlistModal.rule.name')}]}
                    >
                        <Input placeholder={t('site.waitlistModal.placeholder.name')} size="large"/>
                    </Form.Item>
                    <Form.Item
                        name="email"
                        rules={[
                            {required: true, message: t('site.waitlistModal.rule.email')},
                            {type: 'email', message: t('site.waitlistModal.rule.emailType')}
                        ]}
                    >
                        <Input placeholder={t('site.waitlistModal.placeholder.email')} size="large"/>
                    </Form.Item>
                    <Form.Item
                        name="direction"
                        rules={[{required: true, message: t('site.waitlistModal.rule.direction')}]}
                    >
                        <Select
                            placeholder={t('site.waitlistModal.placeholder.direction')}
                            size="large"
                            options={[
                                {label: t('site.waitlistModal.direction.assist'), value: 'AI 辅助交易'},
                                {label: t('site.waitlistModal.direction.auto'), value: '自动化交易'},
                                {label: t('site.waitlistModal.direction.asset'), value: '资产管理'},
                                {label: t('site.waitlistModal.direction.risk'), value: '风控管理'},
                                {label: t('site.waitlistModal.direction.other'), value: '其他'},
                            ]}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </WaitlistContext.Provider>
    );
};
